import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useLocal, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { useIntl, useSelector, shallowEqual, getLocale } from 'umi';
import { formatNum, getSystemColor, handleDate, handleDownload, jumpDetail } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import getSystemLegend from "@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils";
import { getSystemFlag } from '../../_config/system';
import { dateFormat, getSumValue, useComponontState, useConstant } from './_hooks';
import CardBox from '@/pages/dashboard/components/CardBox';
import { OverflowBubbleText } from '@/pages/dashboard/components/OverflowBubbleText';

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableCard.scss";
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getBaseTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';
import ACard from '@/pages/dashboard/components/ACard';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import { resolveConfigString, resolveAndSelectName, prepareMetricsDataSource } from '@/pages/dashboard/utils/metricUtils';
import { IResolvedMetricConfig, IMetricsUserCompositionTrendsCoProps, IMultiMetricConfig, IComponentSegment } from './_type';
import { getName } from '@home/utils/common';

const MetricsUserCompositionTrendsCo = (props: IMetricsUserCompositionTrendsCoProps) => {
  const { config, metricsData, metricsInfo, changeData, segmentDataKey, currentSegmentValue } = props;
  const contextObj = useContext(FilterContext);
  const { filterVal = {} } = contextObj || {};
  const { timeType, metricType, titleEn, titleCn, showComList = ['eyes', 'barChart', 'lineChart', 'sheet'],
    initChartIndex = 1, jumpUrl, yAxisFormatRule, customSumName, tooltipConfig, componentSegment, metricEnName, metricCnName, formatRule, multiMetrics, yAxisNameConfig, showSum, showTooltipRatio, tooltipSort, segmentDataKey: configSegmentDataKey, chartHeight, tableScrollHeight= 247, operationInTitle, noStack, tooltipSegmentLabel } = config || {};
  const PUBLIC_HEIGHT = chartHeight || 290;

  const isPlatform = metricType === 'system';
  const isAu = metricType ? ['dau', 'wau', 'mau']?.includes(metricType) : false;
  const isMultiMetricsMode = multiMetrics && Array.isArray(multiMetrics) && multiMetrics.length > 0;
  // multiMetrics 模式下，如果所有 series 都是 bar 类型，仍然允许显示堆叠 total label
  const isAllBarInMultiMetrics = isMultiMetricsMode && multiMetrics.every((m: IMultiMetricConfig) => !m.chartType || m.chartType === 'bar');
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEn = getLocale() === 'en-US';
  const { selector, gameCode } = useSelector((state: any) => ({
    selector: state?.dashboardSetting?.selector ?? {},
    gameCode: state.dashboard?.currentGame?.game_code
  }), shallowEqual);

  const { getWeekDayStr } = useWeekDay();
  const metiricsMsg = useConstant(timeType);
  const { data, loading, errorMsg: error } = metricsData || {};

  const componentRenderObj = useComponontState(data, metiricsMsg, { ...config, metricsInfo }, isEn);
  const { isNoData, legendData, xAxisData, barSeries, lineSeries, areaSeries, tableList, tableOption, yAxisConfig } = componentRenderObj || {};
  const [iseye, setIseye] = useState(showComList?.includes('eyes'));
  // 使用传入的 currentSegmentValue 或默认值初始化
  const [selectedSegment, setSelectedSegment] = useState(currentSegmentValue || componentSegment?.[0]?.value || '');
  const barChartRef = useRef<any>();
  const lineChartRef = useRef<any>();

  const { isEN } = useLocal();

  // 获取实际使用的 segmentDataKey
  const actualSegmentDataKey = segmentDataKey || configSegmentDataKey;

  const resolvedMetricConfig = useMemo<IResolvedMetricConfig | null>(() => {
    if (!metricsInfo || (!metricEnName && !metricCnName && !formatRule)) {
      return null;
    }
    return {
      metricEnName: resolveConfigString(metricEnName, metricsInfo),
      metricCnName: resolveConfigString(metricCnName, metricsInfo),
      formatRule: resolveConfigString(formatRule, metricsInfo),
    };
  }, [metricsInfo, metricEnName, metricCnName, formatRule]);

  // 解析多指标配置
  const resolvedMultiMetrics = useMemo(() => {
    if (!isMultiMetricsMode || !metricsInfo) return null;
    const metricsDataSource = prepareMetricsDataSource(metricsInfo);
    return multiMetrics.map((metric: IMultiMetricConfig) => ({
      key: metric.key,
      yAxisIndex: metric.yAxisIndex ?? 0,
      enName: resolveConfigString(metric.enName, metricsInfo) || metricsDataSource?.metricsInfo?.[metric.key]?.en || metric.key,
      cnName: resolveConfigString(metric.cnName, metricsInfo) || metricsDataSource?.metricsInfo?.[metric.key]?.cn || metric.key,
      formatter: resolveConfigString(metric.formatter, metricsInfo) || metricsDataSource?.metricsInfo?.[metric.key]?.formatter || 'thousandsInit',
      showLabel: metric.showLabel,
      labelFormatter: metric.labelFormatter ? resolveConfigString(metric.labelFormatter, metricsInfo) : undefined,
    }));
  }, [isMultiMetricsMode, multiMetrics, metricsInfo]);

  const metricTitleName = useMemo(() => resolveAndSelectName(isEn, titleEn, titleCn, metricsInfo, undefined, metiricsMsg?.title?.[metricType || ''] || '') || '', [isEn, titleEn, titleCn, metricsInfo, metiricsMsg?.title, metricType]);

  // 防止切换国际化时,浮窗不会同步国际化
  useEffect(() => {
    barChartRef.current?.refreshChart();
    lineChartRef.current?.refreshChart();
  }, [isEn]);

  const jumpToLink = () => {
    if (jumpUrl) {
      const formValue = {
        filterValue: filterVal,
        timeType,
        diff: filterVal?.diff
      };
      jumpDetail(`/dashboard/game/${gameCode}${jumpUrl}`, formValue);
    }
  };

  const title = useMemo(() => {
    const titleMsg = metricTitleName;
    if (jumpUrl) {
      return (
        <ACard url={`/dashboard/game/${gameCode}${jumpUrl}`}>
          <div className='overlay-line-title-flex' onClick={jumpToLink}>
            <div className='dbd-overlay-line-title'>
              <div className='overlay-line-title'>{titleMsg}</div>
              <SvgIcon className='overlay-line-title-icon' type='arrow-right'></SvgIcon>
            </div>
          </div>
        </ACard>
      );
    }
    return titleMsg;
  }, [jumpUrl, gameCode, jumpToLink, metricTitleName]);

  const options = useMemo(() => componentSegment?.map((item: IComponentSegment) => {
    const { enName, cnName, value } = item;
    return {
      text: isEN ? enName : cnName,
      value,
    };
  }), [componentSegment, isEN]);

  // 处理表格列配置，为 header 添加溢出检测
  const processedTableOption = useMemo(() => {
    if (!tableOption) return [];
    return tableOption.map((column: any) => {
      // 如果 header 是字符串，包装成带溢出检测的组件
      if (typeof column.header === 'string') {
        return {
          ...column,
          header: <OverflowBubbleText text={column.header} placement="top" />,
        };
      }
      return column;
    });
  }, [tableOption]);

  // 浮窗 - 支持多指标模式
  const getTooltipText = (params: any) => {
    // 多指标模式浮窗
    if (isMultiMetricsMode && resolvedMultiMetrics) {
      const tooltipData = [...params];

      const total = showTooltipRatio
        ? tooltipData.reduce((acc, { value }) => acc + (parseFloat(value) || 0), 0)
        : 0;

      // 构建浮窗数据
      const formattedData = tooltipData.map(({ seriesName, value, color }) => {
        const metricConfig = resolvedMultiMetrics.find((m: any) => 
          (isEn ? m.enName : m.cnName) === seriesName
        );
        const formattedValue = metricConfig?.formatter 
          ? dataFormat({ value, formatRule: metricConfig.formatter })
          : formatNum(value, false, true);

        const parsedValue = parseFloat(value);
        const ratioNum = (showTooltipRatio && total > 0 && !isNaN(parsedValue))
          ? dataFormat({ value: parsedValue / total, formatRule: 'percent1Decimal' })
          : undefined;
        
        return {
          name: seriesName,
          metricsNum: formattedValue,
          icon: '',
          color,
          customColor: '',
          ...(ratioNum !== undefined ? { ratioNum } : {}),
        };
      });

      let sumName = isEn ? 'Total' : '总计';
      if (customSumName) {
        const rawName = isEn ? customSumName.enName : customSumName.cnName;
        sumName = resolveConfigString(rawName, metricsInfo) || rawName || sumName;
      }

      // 开启 tooltipSegmentLabel 时，从 componentSegment 中获取当前 tab 的文案作为 tooltip header
      let segLabel = '';
      if (tooltipSegmentLabel && componentSegment) {
        const segItem = componentSegment.find((s: IComponentSegment) => s.value === currentSegmentValue);
        segLabel = segItem ? (isEn ? segItem.enName : segItem.cnName) : '';
      }

      return getEchartTooltipText({
        title: getWeekDayStr(moment(`${params[0].axisValue}`).format(timeType === 'MONTH' ? 'YYYY-MM' : 'YYYY-MM-DD'), timeType || 'DAY'),
        metricsName: segLabel,
        hasSum: !!showSum,
        sumName: showSum ? sumName : '',
        sumNum: showSum ? getSumValue(params, false) : 0,
        hasRatio: !!showTooltipRatio,
        ratioName: showTooltipRatio ? (isEn ? 'Share' : '占比') : '',
        data: formattedData,
        isSort: tooltipSort ?? false,
        isRemoveNull: true,
      });
    }

    // 原有的 tooltipConfig 逻辑
    const {
      valueNameCn: defaultValueNameCn,
      valueNameEn: defaultValueNameEn,
      shareNameCn,
      shareNameEn,
      sumNameCn,
      sumNameEn,
      // 实际之前没有配置和使用过valueFormatRule,这里做兜底引入和赋值处理,防止后续配置了但是不生效
      valueFormatRule: tooltipValueFormatRule,
      showWeek = true,
    } = tooltipConfig || {};
    const valueNameCn = resolvedMetricConfig?.metricCnName || defaultValueNameCn;
    const valueNameEn = resolvedMetricConfig?.metricEnName || defaultValueNameEn;
    // 新配置优先,旧配置兜底,默认值
    const valueFormatRule = resolvedMetricConfig?.formatRule || tooltipValueFormatRule || 'thousandsInit';
    return getBaseTooltip({
      params,
      options: {
        isEn,
        valueNameCn,
        valueNameEn,
        valueFormatRule,
        shareNameCn,
        shareNameEn,
        sumNameCn,
        sumNameEn,
        formatFirstThText: showWeek ? getWeekDayStr : (date: string) => moment(date)?.format('YYYY-MM')
      }
    });
  };

  const formatterFunc = (params: any) => {
    if (tooltipConfig || isMultiMetricsMode) {
      return getTooltipText(params);
    }
    // 'New Users' sort
    let data = isPlatform ? [...params].sort((f, s) => s.value - f.value) : [...params];
    // 使用 resolvedMetricConfig 的 formatRule 格式化数值(如果存在)
    const formatValue = (value: number) => {
      if (resolvedMetricConfig?.formatRule) {
        return dataFormat({ value, formatRule: resolvedMetricConfig.formatRule });
      }
      return formatNum(value, false, true);
    };
    data = data.map(({ seriesName, value, color }) => ({
      name: seriesName,
      metricsNum: formatValue(value),
      icon: isPlatform ? getSystemFlag(seriesName) : '',
      color,
      customColor: isPlatform ? getSystemColor(seriesName.toLowerCase().replace(/\s/g, '')) : '',
    }));

    let sumName = isAu ? metiricsMsg?.au : metiricsMsg?.new;
    // 自定义浮窗合计名称
    if (customSumName) {
      sumName = isEn ? customSumName.enName : customSumName.cnName;
    }

    return getEchartTooltipText({
      title: getWeekDayStr(moment(`${params[0].axisValue}`).format(timeType === 'MONTH' ? 'YYYY-MM' : 'YYYY-MM-DD'), timeType),
      metricsName: '',
      hasSum: true,
      sumName,
      sumNum: getSumValue(params, false),
      data,
      isSort: true,
      isRemoveNull: true,
    });
  };

  // 下载
  const downloadFun = useMemo(() => (
    () => {
      if (isNoData) return;
      const metricsDataSource = prepareMetricsDataSource(metricsInfo);
      const columnMap = new Map<string, any>();
      (tableOption || []).forEach((column: any) => {
        if (column?.key) {
          columnMap.set(column.key, column);
        }
      });
      const downloadData = tableList?.map((item: Record<string, any>) => {
        const formattedItem: Record<string, any> = {};
        Object.keys(item).forEach((key) => {
          const rawValue = item[key];
          // 日期列单独处理
          if (key === 'date') {
            formattedItem[key] = rawValue ? handleDate(rawValue, timeType as string) : '-';
            return;
          }

          // 1. 优先取列配置里拼装好的 formatter（比如 new_users/retention_users）
          const columnConfig = columnMap.get(key);
          const columnFormatter =
            columnConfig?.formatter
            || columnConfig?.formatRule
            || columnConfig?.format;

          if (columnFormatter) {
            formattedItem[key] = dataFormat({ value: rawValue, formatRule: columnFormatter });
            return;
          }

          // 2. 其次看 metricsInfo 里是否给该字段配置了 formatter
          const metricsFormatter = metricsDataSource?.metricsInfo?.[key]?.formatter;
          if (metricsFormatter) {
            formattedItem[key] = dataFormat({ value: rawValue, formatRule: metricsFormatter });
            return;
          }

          // 3. 再次兜底主指标的 formatRule（保持新配置主指标列的格式）
          if (resolvedMetricConfig?.formatRule) {
            formattedItem[key] = dataFormat({ value: rawValue, formatRule: resolvedMetricConfig.formatRule });
            return;
          }

          // 4. 默认回退到 formatNum（旧逻辑）
          formattedItem[key] = formatNum(rawValue, false, true);
        });

        return formattedItem;
      });

      const tableConfig = {
        option: tableOption,
        downloadData,
      };
      handleDownload(tableConfig, metricTitleName);
    }
  ), [
    isNoData,
    tableOption,
    tableList,
    timeType,
    metricTitleName,
    resolvedMetricConfig,
    metricsInfo,
  ]);

  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: 
      <>
        {yAxisNameConfig && <div className="dbd-change-analysis-country-region-chart-y-axis-name">
          <div>
            {getName(isEN, dynamicString(yAxisNameConfig?.left?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.left?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
          <div style= {{marginRight: 6}}>
            {getName(isEN, dynamicString(yAxisNameConfig?.right?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.right?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
        </div>
        }
        <BarChart key={String(isEn)}
          ref={barChartRef}
          height={yAxisNameConfig ? PUBLIC_HEIGHT-18 : PUBLIC_HEIGHT}
          legendData={xAxisData?.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData?.length > 0 ? barSeries : []}
          formatterFunc={formatterFunc}
          isStackBar={!noStack && iseye && (!isMultiMetricsMode || isAllBarInMultiMetrics)}
          setOtherConfigFunc={(opt: any) => {
            /* eslint-disable no-param-reassign */
            // Y轴formatter - 支持双Y轴
            if (yAxisConfig) {
              // 双Y轴模式
              const leftAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 0)?.formatter;
              const rightAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 1)?.formatter;
              
              opt.yAxis = [
                {
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = leftAxisFormatter 
                        ? (metricsInfo ? dynamicString(leftAxisFormatter, prepareMetricsDataSource(metricsInfo), '') : leftAxisFormatter)
                        : yAxisFormatRule;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      type: 'dashed',
                    },
                  },
                },
              ];
              
              // 如果有右Y轴配置
              if (rightAxisFormatter) {
                opt.yAxis.push({
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = metricsInfo 
                        ? dynamicString(rightAxisFormatter, prepareMetricsDataSource(metricsInfo), '')
                        : rightAxisFormatter;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: false,
                  },
                });
              }
            } else {
              // 单Y轴模式
              if (yAxisFormatRule) {
                if (!opt.yAxis) opt.yAxis = {};
                if (!opt.yAxis.axisLabel) opt.yAxis.axisLabel = {};

                opt.yAxis.axisLabel.formatter = function (value: string) {
                  return dataFormat({ value, formatRule: yAxisFormatRule });
                };
              }
            }
            
            // 处理 label (Copied from lineChart logic)
            if (isMultiMetricsMode && resolvedMultiMetrics) {
              const visibleIndices = resolvedMultiMetrics
                .map((m: any, i: number) => (m.showLabel ? i : -1))
                .filter((i: number) => i !== -1);

              if (visibleIndices.length > 0) {
                opt = addChartLabel(opt, {
                  show: iseye,
                  formatNum: (v: any) => String(v),
                  formatter: (params: any) => {
                    const {seriesIndex} = params;
                    const metricConfig = resolvedMultiMetrics[seriesIndex];
                    const formatRule = metricConfig?.labelFormatter || metricConfig?.formatter || 'thousandsInit';
                    return dataFormat({ value: params.value, formatRule });
                  },
                }, visibleIndices);
              }
            }

            // X轴formatter
            opt.xAxis[0].axisLabel.formatter = function (value: string) {
              return dateFormat(timeType, value);
            };
            opt = xAxisHighLight(opt, {
              formatter: (value: { value: string; }) => dateFormat(timeType, value?.value)
            });
            // pc console游戏os来源legend显示图标
            getSystemLegend(opt, selector);
            opt.legend.bottom = -4;
            /* eslint-enable no-param-reassign */
            return opt;
          }}
        />
      </>
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: 
      <>
        {yAxisNameConfig && <div className="dbd-change-analysis-country-region-chart-y-axis-name">
          <div>
            {getName(isEN, dynamicString(yAxisNameConfig?.left?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.left?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
          <div style= {{marginRight: 6}}>
            {getName(isEN, dynamicString(yAxisNameConfig?.right?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.right?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
        </div>
        }
        <LineChart key={String(isEn)}
          ref={lineChartRef}
          height={yAxisNameConfig ? PUBLIC_HEIGHT-18 : PUBLIC_HEIGHT}
          legendData={xAxisData?.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData?.length > 0 ? lineSeries : []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
          /* eslint-disable no-param-reassign */
          // Y轴formatter - 支持双Y轴
            if (yAxisConfig) {
            // 双Y轴模式
              const leftAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 0)?.formatter;
              const rightAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 1)?.formatter;

              opt.yAxis = [
                {
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = leftAxisFormatter
                        ? (metricsInfo ? dynamicString(leftAxisFormatter, prepareMetricsDataSource(metricsInfo), '') : leftAxisFormatter)
                        : yAxisFormatRule;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      type: 'dashed',
                    },
                  },
                },
              ];

              // 如果有右Y轴配置
              if (rightAxisFormatter) {
                opt.yAxis.push({
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = metricsInfo
                        ? dynamicString(rightAxisFormatter, prepareMetricsDataSource(metricsInfo), '')
                        : rightAxisFormatter;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: false,
                  },
                });
              }
            } else {
              // 单Y轴模式
              if (yAxisFormatRule) {
                if (!opt.yAxis) opt.yAxis = {};
                if (!opt.yAxis.axisLabel) opt.yAxis.axisLabel = {};

                opt.yAxis.axisLabel.formatter = function (value: string) {
                  return dataFormat({ value, formatRule: yAxisFormatRule });
                };
              }
            }

            // 设置series的z-index,第一个series在最上层
            if (opt.series && opt.series.length > 0) {
              opt.series.forEach((seriesItem: any, index: number) => {
                seriesItem.z = opt.series.length - index;
              });
            }

            // 处理 label
            if (isMultiMetricsMode && resolvedMultiMetrics) {
              const visibleIndices = resolvedMultiMetrics
                .map((m: any, i: number) => (m.showLabel ? i : -1))
                .filter((i: number) => i !== -1);

              if (visibleIndices.length > 0) {
                opt = addChartLabel(opt, {
                  show: iseye,
                  formatNum: (v: any) => String(v),
                  formatter: (params: any) => {
                    const {seriesIndex} = params;
                    const metricConfig = resolvedMultiMetrics[seriesIndex];
                    const formatRule = metricConfig?.labelFormatter || metricConfig?.formatter || 'thousandsInit';
                    return dataFormat({ value: params.value, formatRule });
                  },
                }, visibleIndices);
              }
            }

            // X轴formatter
            opt.xAxis[0].axisLabel.formatter = function (value: string) {
              return dateFormat(timeType, value);
            };
            opt = xAxisHighLight(opt, {
              formatter: (value: { value: string; }) => dateFormat(timeType, value?.value)
            });
            // pc console游戏os来源legend显示图标
            getSystemLegend(opt, selector);
            opt.legend.bottom = -4;

            opt.grid = {
              ...opt.grid,
              left: 4
            };
            return opt;
          } } /></>
    },
    {
      type: 'stackLine',
      svgType: 'stack-diagram',
      content: formatMessage({ id: 'common_stack_line_chart' }),
      component: 
      <>
        {yAxisNameConfig && <div className="dbd-change-analysis-country-region-chart-y-axis-name">
          <div>
            {getName(isEN, dynamicString(yAxisNameConfig?.left?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.left?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
          <div style= {{marginRight: 6}}>
            {getName(isEN, dynamicString(yAxisNameConfig?.right?.en, prepareMetricsDataSource(metricsInfo), ''), dynamicString(yAxisNameConfig?.right?.cn, prepareMetricsDataSource(metricsInfo), ''))}
          </div>
        </div>
        }
        <LineChart key={String(isEn)}
          height={yAxisNameConfig ? PUBLIC_HEIGHT-18 : PUBLIC_HEIGHT}
          legendData={xAxisData?.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData?.length > 0 ? areaSeries : []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
          /* eslint-disable no-param-reassign */
          // Y轴formatter - 支持双Y轴
            if (yAxisConfig) {
            // 双Y轴模式
              const leftAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 0)?.formatter;
              const rightAxisFormatter = yAxisConfig.find((cfg: any) => cfg.yAxisIndex === 1)?.formatter;

              opt.yAxis = [
                {
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = leftAxisFormatter
                        ? (metricsInfo ? dynamicString(leftAxisFormatter, prepareMetricsDataSource(metricsInfo), '') : leftAxisFormatter)
                        : yAxisFormatRule;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      type: 'dashed',
                    },
                  },
                },
              ];

              // 如果有右Y轴配置
              if (rightAxisFormatter) {
                opt.yAxis.push({
                  type: 'value',
                  axisLabel: {
                    formatter: (value: string) => {
                      const formatRule = metricsInfo
                        ? dynamicString(rightAxisFormatter, prepareMetricsDataSource(metricsInfo), '')
                        : rightAxisFormatter;
                      return dataFormat({ value, formatRule });
                    },
                  },
                  splitLine: {
                    show: false,
                  },
                });
              }
            } else {
              // 单Y轴模式
              if (yAxisFormatRule) {
                if (!opt.yAxis) opt.yAxis = {};
                if (!opt.yAxis.axisLabel) opt.yAxis.axisLabel = {};

                opt.yAxis.axisLabel.formatter = function (value: string) {
                  return dataFormat({ value, formatRule: yAxisFormatRule });
                };
              }
            }

            // 设置series的z-index,第一个series在最上层
            if (opt.series && opt.series.length > 0) {
              opt.series.forEach((seriesItem: any, index: number) => {
                seriesItem.z = opt.series.length - index;
              });
            }

            // X轴formatter
            opt.xAxis[0].axisLabel.formatter = function (value: string) {
              return dateFormat(timeType, value);
            };
            opt = xAxisHighLight(opt, {
              formatter: (value: { value: string; }) => dateFormat(timeType, value?.value)
            });
            // pc console游戏os来源legend显示图标
            getSystemLegend(opt, selector);
            opt.legend.bottom = -4;
            /* eslint-enable no-param-reassign */
            return opt;
          } } />
      </>
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={PUBLIC_HEIGHT}
        loading={loading}
        tableOption={processedTableOption}
        tableList={tableList}
        scrolLableVal={{
          maxHeight: tableScrollHeight,
          minHeight: tableScrollHeight,
        }}
      />
    },
  ]?.filter((item) => showComList?.includes(item?.type));

  return (
    <CardBox
      className='register-table'
      height={PUBLIC_HEIGHT}
      bordered={false}
      titleCom={title}
      segmentOption={options}
      selectedSegment={selectedSegment}
      operationInTitle={!!operationInTitle}
      setSelectedSegment={(value: string) => {
        setSelectedSegment(value);
        // 如果配置了 segmentDataKey 且有 changeData，则触发数据更新
        if (actualSegmentDataKey && changeData) {
          changeData(actualSegmentDataKey, value);
        }
      }}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye);
        }
      }}
      renderList={renderList || []}
      defaultAvtive={initChartIndex}
      downloadClick={downloadFun}
      isLoading={loading}
      isError={error}
      isNodata={isNoData}
    />
  );
};
export default MetricsUserCompositionTrendsCo;